<template>
  <page-container>
    <template v-slot:page-fixed-header>
      <span class="text-h5 font-weight-bold ml-3 full-width page-header">{{
        $t("search.searchResult")
      }}</span>
      <v-spacer></v-spacer>
    </template>
    <template v-slot:page-content>
      <v-row v-if="searchItems.length" justify="center">
        <v-col v-for="item in searchItems" :key="item.origin" cols="12">
          <v-card>
            <v-toolbar color="primaryDark" dark height="48px">
              <v-icon size="20" class="mr-2">{{
                getSectionIcon(item.origin)
              }}</v-icon>
              <v-toolbar-title>
                {{ getSectionTitle(item.origin) }}</v-toolbar-title
              >
              <v-spacer></v-spacer>
              <document-header-filter
                v-if="item.origin === 'document'"
              ></document-header-filter>
              <span class="mx-3" style="font-size: 14px">
                {{ $t("search.currentResult") }}: {{ item.value.length }}
                {{ $t("search.of") }} {{ item.count }}
              </span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <a-btn
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    :loading="loading"
                    :disabled="item.value.length === item.count"
                    small
                    @click="onViewMoreClick(item)"
                  >
                    {{ $t("search.viewMore") }}
                  </a-btn>
                </template>
                <span>{{ $t("search.viewMore") }}</span>
              </v-tooltip>
            </v-toolbar>

            <v-container fluid>
              <v-row dense>
                <meeting-search-card
                  v-if="item.origin === 'meeting'"
                  :data="item.value"
                />
                <agenda-search
                  v-if="
                    item.origin === 'agenda' &&
                    $security.checkUserAccess('ViewAgenda')
                  "
                  :items="item.value"
                ></agenda-search>
                <document-search
                  v-if="item.origin === 'document'"
                  :items="item.value"
                ></document-search>
                <people-search
                  v-if="
                    item.origin === 'people' &&
                    $security.checkUserAccess('ViewPeople')
                  "
                  :items="item.value"
                ></people-search>
                <survey-search
                  v-if="
                    item.origin === 'survey' &&
                    $security.checkUserAccess('ViewSurveyPolls')
                  "
                  :items="item.value"
                ></survey-search>
                <task-search
                  v-if="
                    item.origin === 'task' &&
                    $security.checkUserAccess('ViewTask')
                  "
                  style="width: 100%"
                  :items="item.value"
                ></task-search>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else justify="center">
        <v-card v-if="blNoDataFound" flat class="mx-auto my-4" max-width="344">
          <v-card-text>
            <p class="text-h4 text--primary">
              {{ $t("agenda.hRecordNotFound") }}
            </p>
          </v-card-text>
        </v-card>
      </v-row>

      <Loader :loading="Loaderloading" :message="LoaderText"></Loader>
    </template>
  </page-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import MeetingSearchCard from "../components/search/MeetingSearchCard.vue";
import SurveyGrid from "../components/poll/SurveyGrid.vue";
import DocumentSearch from "../components/search/DocumentSearch.vue";
import AgendaSearch from "../components/search/AgendaSearch.vue";
import PeopleSearch from "../components/search/PeopleSearch.vue";
import SurveySearch from "../components/search/SurveySearch.vue";
import Loader from "../components/Loader.vue";
import TaskSearch from "../components/search/TaskSearch.vue";
import DocumentHeaderFilter from "../components/search/DocumentHeaderFilter.vue";

const preventRounts = ["Meeting", "Profile", "SurveyDetails"];

export default {
  name: "GlobalSearch",
  components: {
    MeetingSearchCard,
    SurveyGrid,
    DocumentSearch,
    AgendaSearch,
    PeopleSearch,
    SurveySearch,
    Loader,
    TaskSearch,
    DocumentHeaderFilter,
  },
  data() {
    return {
      loading: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    if (!preventRounts.includes(to.name)) {
      this.setSearchText("");
      this.setSearchResult([]);
    }
    next();
  },
  computed: {
    ...mapState({
      searchResult: (state) => state.search.searchResult,
      searchText: (state) => state.search.searchText,
      Loaderloading: (state) => state.search.loaderLoading,
    }),
    searchItems() {
      return this.searchResult.filter(
        (obj) => !(obj.count === 0 && obj.value.length === 0)
      );
    },
    LoaderText() {
      return this.$t("LoaderText");
    },
    blNoDataFound() {
      if (this.Loaderloading) {
        return false;
      }
      return this.searchItems.length === 0;
    },
  },
  async mounted() {
    this.setLoader(true);
    try {
      const canSurvey = this.$security.checkUserAccess("ViewSurveyPolls");
      const canPeople = this.$security.checkUserAccess("ViewPeople");

      const promises = [
        this.getTenantConfigList(),
        this.getMeetingListApi(false),
        this.getAgendaListApi(),
      ];
      if (canSurvey) promises.push(this.getSurveyList());
      if (canPeople) promises.push(this.getPeopleList());

      await Promise.all(promises);
    } finally {
      this.setLoader(false);
    }
  },
  methods: {
    ...mapActions({
      getMeetingListApi: "meeting/getMeetingList",
      getAgendaListApi: "agenda/getAgendaList",
      getTenantConfigList: "tenant/getTenantConfigList",
      getSearchResultsAPI: "search/getSearchResults",
      getPeopleList: "people/getPeopleList",
      getSurveyList: "poll/getSurveyList",
      getDocumentsList: "document/getDocumentsList",
    }),
    ...mapMutations({
      setSearchResult: "search/GET_SEARCH_RESULT",
      setSearchText: "search/SET_SEARCH_TEXT",
      setLoader: "search/SET_LOADER",
    }),
    onViewMoreClick,
    capitalize,
    getSectionIcon,
    getSectionTitle,
    getSearchResults,
  },
};

async function onViewMoreClick(item) {
  this.loading = true;
  const query = {
    q: this.searchText,
    skip: item.value.length,
    origin: item.origin,
  };
  await this.getSearchResults(query);
  this.loading = false;
}

async function getSearchResults(query) {
  this.loading = true;
  await this.getSearchResultsAPI(query);
  this.loading = false;
}

function capitalize(value) {
  return value.replace(/(?:^|\s|-)\S/g, (x) => x.toUpperCase());
}

function getSectionIcon(name) {
  switch (name) {
    case "agenda":
      return "mdi-calendar-text";
    case "survey":
      return "mdi-poll";
    case "meeting":
      return "mdi-calendar";
    case "people":
      return "mdi-account-group";
    case "document":
      return "mdi-file-document-multiple";
    case "task":
      return "mdi-book-edit";
    default:
      return "";
  }
}
function getSectionTitle(name) {
  switch (name) {
    case "agenda":
      return this.$t("search.agendaTitle");
    case "survey":
      return this.$t("menu.survey");
    case "meeting":
      return this.$t("meetings");
    case "people":
      return this.$t("menu.people");
    case "document":
      return this.$t("menu.documents");
    case "task":
      return this.$t("task.lblTask");
    default:
      return "";
  }
}
</script>

<style>
</style>